import type { SimMeasurableParam, SimOverride, SimProfile } from "@opus-dt/shared-types";
import {
  applyPairProfile,
  deltaPctOf,
  effectiveFeedRate,
  originOf,
  SIM_DEFAULTS,
} from "@opus-dt/shared-types";
import type { SimProfileDraft } from "@/types/simulations.types";
import type { SandboxMachine } from "@/types/sim-sandbox.types";
import { Pill, type PillTone } from "@/components/ui/pill";
import {
  PARAM_TYPES,
  OVERRIDE_LIMIT_PCT,
  clampDelta,
  removeOverride,
  upsertOverride,
} from "./param-types";

interface Props {
  machine: SandboxMachine;
  onChange: (patch: { simProfile?: SimProfile; simOverrides?: SimOverride[] }) => void;
}

// Defaults do conjunto ÚNICO compartilhado (shared-types sim-defaults);
// base do slider quando o parâmetro não veio medido/autorado (±25%). Ciclo não tem
// default próprio: é DERIVADO do ritmo (3600 feed).
const DEFAULTS: Record<string, number> = {
  feedRateUnH: SIM_DEFAULTS.feedRateUnH,
  mtbfMin: SIM_DEFAULTS.mtbfMin,
  mttrMin: SIM_DEFAULTS.mttrMin,
  qualityTargetPct: SIM_DEFAULTS.qualityTargetPct,
};

// Origem por PARÂMETRO: os 4 deriváveis (mtbf/mttr + ritmo/qualidade via
// contador de produção) leem a origem do mapa provenanceByParam (originOf cobre o escalar
// legado). Ciclo HERDA a origem do ritmo. Ajustar o valor (delta ≠ 0) vira premissa.
function paramOrigin(
  machine: SandboxMachine,
  paramId: string,
  adjusted: boolean
): {
  label: string;
  tone: PillTone;
} {
  if (adjusted) return { label: "premissa", tone: "neutral" };
  const param = (paramId === "cycleTime" ? "feedRate" : paramId) as SimMeasurableParam;
  // O par (máquina×produto) é um tier da cascata: aplicar o mesmo helper aqui mantém o
  // badge honesto sobre QUAL valor a rodada vai usar (senão diria "premissa" num valor autorado).
  const src = applyPairProfile(
    { ...(machine.simProfile ?? machine.measuredBase ?? {}) },
    machine.pairProfile
  );
  const prov = originOf(src, param).provenance;
  if (prov === "measured") return { label: "medido", tone: "success" };
  if (prov === "product") return { label: "por produto", tone: "info" };
  if (prov === "similar") return { label: "similar", tone: "warning" };
  return { label: "premissa", tone: "neutral" };
}

function fmt(field: string, v: number): string {
  if (field === "cycleTimeS") return v.toFixed(2);
  if (field === "qualityTargetPct") return v.toFixed(1);
  return String(Math.round(v));
}

// Os params de simulação como SLIDERS: valor efetivo + badge de origem, faixa
// travada a ±25% da base (o slider ajusta o override, preservando a base medida). Card por param.
// O CICLO deixou de ser input: card derivado read-only (3600/ritmo).
export function SimParamFields({ machine, onChange }: Props) {
  const profile: SimProfileDraft = machine.simProfile ?? {};
  const overrides: SimOverride[] = machine.simOverrides ?? [];
  const deltaOf = (id: SimOverride["param"]) => deltaPctOf(overrides, id);

  const setDelta = (param: SimOverride["param"], deltaPct: number) =>
    onChange({
      simOverrides:
        deltaPct === 0
          ? removeOverride(overrides, param)
          : upsertOverride(overrides, param, deltaPct),
    });

  // RITMO é a grandeza canônica. Cenário LEGADO só com ciclo converte
  // (3600/ciclo) como base de exibição; override `cycleTime` legado entra como fator INVERSO
  // no ritmo efetivo (espelha o worker). Autoria nova não emite ciclo.
  const feedBase =
    profile.feedRateUnH ??
    (profile.cycleTimeS ? 3600 / profile.cycleTimeS : undefined) ??
    machine.measuredBase?.feedRateUnH ??
    DEFAULTS.feedRateUnH!;
  const feedAdjusted = deltaOf("feedRate") !== 0 || deltaOf("cycleTime") !== 0;
  const feedEffective = effectiveFeedRate(feedBase, deltaOf("feedRate"), deltaOf("cycleTime"));

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-muted-foreground">
        Ajustar um valor troca a origem para <strong>premissa</strong> — isso fica visível e entra
        no nível de confiança do resultado. A faixa é limitada a ±{OVERRIDE_LIMIT_PCT}% da base.
      </p>

      {PARAM_TYPES.map((p) => {
        // Ciclo derivado, somente leitura; herda a origem do ritmo.
        if (p.id === "cycleTime") {
          const origin = paramOrigin(machine, "cycleTime", feedAdjusted);
          return (
            <div key={p.id} className="space-y-1 rounded-md border border-dashed p-3">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium">{p.label}</span>
                <Pill tone={origin.tone} withDot>
                  {origin.label}
                </Pill>
                <span className="ml-auto font-mono text-sm font-semibold tabular-nums">
                  {fmt(p.field, 3600 / feedEffective)}
                  <span className="ml-1 text-[11px] font-normal text-muted-foreground">
                    {p.unit}
                  </span>
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Derivado do ritmo (3600 ÷ ritmo) — ajuste o <strong>Ritmo</strong> para alterar.
              </p>
            </div>
          );
        }
        // Base do slider: perfil autorado → base MEDIDA do preview → default conservador.
        const base =
          p.id === "feedRate"
            ? feedBase
            : (profile[p.field] ?? machine.measuredBase?.[p.field] ?? DEFAULTS[p.field] ?? 1);
        const delta = deltaOf(p.id);
        const effective = base * (1 + delta / 100);
        const min = base * (1 - OVERRIDE_LIMIT_PCT / 100);
        // Qualidade não passa de 100% (o motor rejeita > 100).
        const max =
          p.field === "qualityTargetPct"
            ? Math.min(base * (1 + OVERRIDE_LIMIT_PCT / 100), 100)
            : base * (1 + OVERRIDE_LIMIT_PCT / 100);
        const step = Math.max((max - min) / 40, 0.01);
        const origin = paramOrigin(machine, p.id, delta !== 0);
        const id = `${machine.key}-${p.field}`;
        return (
          <div key={p.id} className="space-y-2 rounded-md border p-3">
            <div className="flex items-center gap-2">
              <label htmlFor={id} className="text-[13px] font-medium">
                {p.label}
              </label>
              <Pill tone={origin.tone} withDot>
                {origin.label}
              </Pill>
              <span className="ml-auto font-mono text-sm font-semibold tabular-nums">
                {fmt(p.field, effective)}
                <span className="ml-1 text-[11px] font-normal text-muted-foreground">{p.unit}</span>
              </span>
            </div>
            <input
              id={id}
              type="range"
              className="w-full accent-primary"
              min={min}
              max={max}
              step={step}
              value={Math.min(Math.max(effective, min), max)}
              onChange={(e) =>
                setDelta(p.id, clampDelta(Math.round((Number(e.target.value) / base - 1) * 100)))
              }
            />
          </div>
        );
      })}

      <div className="rounded-md bg-muted/50 p-2.5 text-[11px] leading-relaxed text-muted-foreground">
        <p>
          <strong>Legenda</strong> — MTBF: tempo médio entre falhas · MTTR: tempo médio de reparo ·
          Ritmo: unidades/hora de alimentação · Ciclo: segundos por unidade · Qualidade: meta de
          peças boas.
        </p>
        <p className="mt-1.5">
          Parâmetros <em>medidos</em> vêm da janela de calibração automática (90 dias); ajuste a
          janela em Período → opções avançadas.
        </p>
      </div>
    </div>
  );
}
